import React, { useMemo } from 'react';
import { useTheme } from '../ThemeProvider';
import Typography from '../Typography';
import './styles.css';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'contained' | 'outlined' | 'text' | 'glass';

  /** Theme-based color to use. Maps to theme.colors.accent.* values */
  color?: 'primary' | 'secondary' | 'error';

  /** Typography-based size variant */
  size?: 'small' | 'medium' | 'large';

  /** Make the button take full width */
  fullWidth?: boolean;

  /** Icon to show before text */
  iconStart?: React.ReactNode;

  /** Icon to show after text */
  iconEnd?: React.ReactNode;

  /** Convert button to anchor tag */
  href?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  className = '',
  iconStart,
  iconEnd,
  children,
  href,
  onClick,
  ...props
}) => {
  const { theme } = useTheme();

  const buttonStyles = useMemo(() => {
    const styles: React.CSSProperties = {
      background: 'var(--panel-glass-bg)',
      backdropFilter: 'blur(12px) saturate(150%)',
      WebkitBackdropFilter: 'blur(12px) saturate(150%)',
      border: '1px solid var(--panel-glass-border)',
      borderRadius: '12px',
      color: theme.colors.fg.primary,
      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    };

    if (disabled) {
      styles.opacity = theme.buttons.disabled.opacity;
      styles.cursor = 'not-allowed';
    }

    return styles;
  }, [theme, disabled]);

  const buttonClasses = [
    'button',
    `button--${variant}`,
    `button--${size}`,
    fullWidth && 'button--full-width',
    disabled && 'button--disabled',
    className
  ].filter(Boolean).join(' ');

  const commonProps = {
    className: buttonClasses,
    style: buttonStyles,
    disabled,
    onClick: disabled ? undefined : onClick,
    ...props
  };

  const content = (
    <Typography
      variant={size === 'small' ? 'body2' : 'body1'}
      component="span"
      className="button__content"
    >
      {iconStart && <span className="button__icon button__icon--start">{iconStart}</span>}
      {children}
      {iconEnd && <span className="button__icon button__icon--end">{iconEnd}</span>}
    </Typography>
  );

  if (href && !disabled) {
    return (
      <a
        href={href}
        {...commonProps as React.AnchorHTMLAttributes<HTMLAnchorElement>}
      >
        {content}
      </a>
    );
  }

  return (
    <button {...commonProps}>
      {content}
    </button>
  );
};